import { Box, styled, Typography } from "@material-ui/core";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { EXAM_LABELS } from "../../../../_lib/epocus-exam-constants";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_PENDING_BG,
  EPOCUS_STATUS_PENDING_TEXT,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../../_lib/epocus-palette";
import { getScoreColor } from "../../../../_lib/qa-score-colors";
import { Card, SectionTitle } from "../../../../_lib/stat-primitives";
import type { RecentExam } from "../../../../_lib/use-performer-stats";
import { linkToEpocusExam } from "../../../../links";

const CELL_STYLE: CSSProperties = {
  padding: "9px 12px",
  textAlign: "left",
  verticalAlign: "middle",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  whiteSpace: "nowrap",
  fontSize: rem(13),
};

const HEAD_CELL_STYLE: CSSProperties = {
  ...CELL_STYLE,
  fontSize: rem(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  backgroundColor: EPOCUS_BG,
};

const STATUS_STYLES: Record<
  RecentExam["status"],
  { bg: string; text: string; label: string }
> = {
  reviewed: {
    bg: EPOCUS_STATUS_REVIEWED_BG,
    text: EPOCUS_STATUS_REVIEWED_TEXT,
    label: "Reviewed",
  },
  pending: {
    bg: EPOCUS_STATUS_PENDING_BG,
    text: EPOCUS_STATUS_PENDING_TEXT,
    label: "Pending",
  },
};

const StatusBadge: FC<{ status: RecentExam["status"] }> = ({ status }) => {
  const { bg, text, label } = STATUS_STYLES[status];
  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: rem(11),
        fontWeight: 700,
        backgroundColor: bg,
        color: text,
      }}
    >
      {label}
    </Box>
  );
};

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString([], {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
};

const ClickableRow = styled("tr")({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: EPOCUS_SELECTED_BG,
  },
});

const RecentExamRow: FC<{ exam: RecentExam }> = ({ exam }) => {
  const navigate = useNavigate();
  const href = linkToEpocusExam(exam.encounterId, exam.examId);

  return (
    <ClickableRow onClick={() => navigate(href)}>
      <td style={{ ...CELL_STYLE, color: EPOCUS_TEXT_SECONDARY }}>
        {formatDate(exam.date)}
      </td>
      <td style={CELL_STYLE}>
        <a
          href={href}
          onClick={(event) => event.stopPropagation()}
          style={{
            color: EPOCUS_TEXT_PRIMARY,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          {exam.patientName ?? "Unnamed patient"}
        </a>
      </td>
      <td style={CELL_STYLE}>{EXAM_LABELS[exam.examType]}</td>
      <td style={CELL_STYLE}>
        <StatusBadge status={exam.status} />
      </td>
      <td
        style={{
          ...CELL_STYLE,
          fontWeight: 700,
          color: getScoreColor(exam.score),
        }}
      >
        {exam.score}
      </td>
    </ClickableRow>
  );
};

export const RecentExamsTable: FC<{ exams: RecentExam[] }> = ({ exams }) => (
  <Box>
    <SectionTitle>Recent Exams</SectionTitle>
    {exams.length === 0 ? (
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        No exams in this range.
      </Typography>
    ) : (
      <Card style={{ padding: 0, overflowX: "auto" }}>
        <table
          style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}
        >
          <thead>
            <tr>
              {["Date", "Patient", "Exam", "Status", "QA score"].map((col) => (
                <th key={col} style={HEAD_CELL_STYLE}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {exams.map((exam) => (
              <RecentExamRow key={exam.examId} exam={exam} />
            ))}
          </tbody>
        </table>
      </Card>
    )}
    {exams.length > 0 && (
      <Typography
        style={{
          fontSize: rem(11),
          color: EPOCUS_TEXT_SECONDARY,
          marginTop: 8,
          paddingLeft: 2,
        }}
      >
        Most recent submitted exams in the selected range. Open one to read the
        worksheet and its review.
      </Typography>
    )}
  </Box>
);
